import { createSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import zipObject from 'lodash/zipObject';
import orderBy from 'lodash/orderBy';
import { DATASET, CURRENT_PRODUCT_MODE, isAppModule } from '@utils/constants';
import { INVERTED_METRIC_SWITCH_MAP, BASE_METRICS } from '@utils/metrics';
import { combineWithMetricDeDuplication } from '@utils/metrics/metricHelpers';
import { SCOPE } from './types';
import { filtersViewSelector } from '../base/selectors';

const selector = (state) => get(state, SCOPE);

// sample
const realtimeFiltersRequestBody = {
  EI: {
    type: 'multitimeseries',
    dataset: DATASET,
    metrics: ['RebufferingRatio'],
    filter: [],
    granularity: 'ALL',
    options: {},
  },
  AI: {
    type: 'multitimeseries',
    dataset: DATASET,
    metrics: ['RebufferingRatio'],
    filter: [],
    granularity: 'ALL',
    options: {},
  },
  APP: {
    type: 'timeseries',
    dataset: DATASET,
    granularity: 'ALL',
    options: {
      withTotals: true,
    },
    interval: '',
    filter: [],
    metrics: [],
    isLiveMode: true,
  },
  ODP: {},
};

export const filterSelector = createSelector(
  filtersViewSelector,
  (filterState) => filterState.appliedFilter,
);

// returns inverted metrics
// used in apis, table
export const metricSelector: any = createSelector(filtersViewSelector, (filterState) =>
  filterState.metrics.map((metric) => get(INVERTED_METRIC_SWITCH_MAP, metric, metric)),
);

// raw metrics as in store (for select fields)
export const rawMetricSelector = createSelector(
  filtersViewSelector,
  (filterState) => filterState.metrics,
);

export const payloadSelector = createSelector(
  metricSelector,
  filterSelector,
  (metrics, filters) => {
    const transformedFilters = isAppModule ? filters.map((i) => i.rule) : [filters];
    const queries = transformedFilters.map((filter) => {
      const query = cloneDeep(realtimeFiltersRequestBody[CURRENT_PRODUCT_MODE]) as any;
      // always include baseline metrics
      query.metrics = combineWithMetricDeDuplication(metrics, BASE_METRICS);
      query.filter = filter;
      return query;
    });
    return {
      queries: queries,
    };
  },
);

export const dataSelector = createSelector(
  selector,
  filterSelector,
  payloadSelector,
  (filterState, filters, payload) => {
    const metrics = get(payload, ['queries', 0, 'metrics'], []);
    const maxData = zipObject(metrics, Array.from({ length: metrics.length }).fill(0));

    if (isAppModule) {
      const data = filters.map((filter, i) => {
        const row = get(filterState, ['response', i, 'totals'], {});
        metrics.forEach((metric) => {
          const value = row[metric] === 'NA' ? 0 : row[metric];
          if (value > maxData[metric]) {
            maxData[metric] = value;
          }
        });
        return {
          filter,
          ...row,
        };
      });
      return {
        data,
        timestamp: get(filterState, ['response', 0, 'data', 0, 'timestamp'], ''),
        maxData,
      };
    }
    // return filterState.response ? filterState.response[0] : {}
    const data = get(filterState, 'response[0].data[0].results', []);
    data.forEach((rowData) => {
      metrics.forEach((metric) => {
        const value = rowData[metric];
        if (value > maxData[metric]) {
          maxData[metric] = value;
        }
      });
    });
    return {
      data,
      timestamp: get(filterState, 'response[0].data[0].timestamp', ''),
      warmup: get(filterState, 'response[0].warmup', []),
      maxData,
    };
  },
);

export const filtersTableDataSortSelector = createSelector(
  filtersViewSelector,
  dataSelector,
  (filtersData, tableData) => {
    const { sort } = filtersData;
    // build row data
    const records = get(tableData, 'data', []).map((row) => {
      return {
        id: row.filter.fid || row.filter.key,
        ...row,
      };
    });

    // sort data
    const sortedData = orderBy(
      records,
      (v) => (v[sort.key] === 'NA' ? 0 : v[sort.key]),
      sort.order,
    );
    return {
      data: sortedData,
      sort,
    };
  },
);

export const loadingSelector = createSelector(selector, (filterState) => ({
  loading: filterState.isFetching,
  errorCode: filterState.errorCode,
  errorMsg: filterState.errorMsg,
}));

export default selector;
